import { ReactElement, useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import valid from "card-validator";

import { useFormStore } from "@/store/form";

import Column from "@/components/Column";
import Input from "@/components/Input";
import Select from "@/components/Select";
import SelectItem, { SelectItemProps } from "@/components/Select/Item";

export const creditCardDataSchema = yup.object().shape({
  payment: yup.object().shape({
    mainName: yup.string().trim().required("Nome é obrigatório"),
    creditCard: yup
      .string()
      .test(
        "credit-card",
        "Cartão de crédito inválido",
        (value) => valid.number(value).isValid
      )
      .required("Cartão de crédito é obrigatório"),
    dueMonth: yup
      .string()
      .required("Vencimento é obrigatório")
      .test(
        "due-Month",
        "Mês inválido",
        (value = "") => valid.expirationMonth(value).isValid
      ),
    dueYear: yup
      .string()
      .required("Vencimento é obrigatório")
      .test(
        "due-Year",
        "Ano inválido",
        (value = "") => valid.expirationYear(value).isValid
      ),
    securityCode: yup
      .string()
      .required("CVV é obrigatório")
      .test("cvv", "CVV inválido", (value) => valid.cvv(value).isValid),
  }),
});

export type CreditCardPaymentFieldsProps = {
  setFormIsValid(isValid: boolean): void;
};

export const CreditCardPaymentFields = ({
  setFormIsValid,
}: CreditCardPaymentFieldsProps) => {
  const payment = useFormStore((store) => store.value.payment);

  const [name, setName] = useState(payment.mainName);
  const [creditCard, setCreditCard] = useState(payment.creditCard);
  const [dueMonth, setDueMonth] = useState(payment.dueMonth);
  const [dueYear, setDueYear] = useState(payment.dueYear);
  const [securityCode, setSecurityCode] = useState(payment.securityCode);
  const [yearsOptions, setYearsOptions] = useState<ReactElement<SelectItemProps>[]>([]);

  const cardInfo = valid.number(creditCard);
  const isAmex = cardInfo.card?.type === 'american-express';
  const cardMask = isAmex ? '9999 999999 99999' : '9999 9999 9999 9999';
  const cvvMask = isAmex ? '9999' : '999';

  useEffect(() => {
    const minCardLength = cardMask.length;
    const minCvvLength = cvvMask.length;
    const formIsValid = !!(
      name?.trim() &&
      creditCard?.length >= minCardLength &&
      dueMonth &&
      dueYear &&
      dueYear !== " " &&
      securityCode?.length >= minCvvLength
    );
    setFormIsValid(formIsValid);
  }, [creditCard, dueMonth, dueYear, name, securityCode, setFormIsValid, cardMask, cvvMask]);

  useEffect(() => {
    const startYear = new Date().getFullYear();

    const options = [];

    options.push(
      <SelectItem key={" "} value={" "}>
        {" "}
      </SelectItem>
    );

    for (let i = 0; i < 10; i++) {
      options.push(
        <SelectItem key={startYear + i} value={startYear + i}>
          {startYear + i}
        </SelectItem>
      );
    }

    setYearsOptions(options);
  }, []);

  const monthOptions = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const selectedYear = parseInt(String(dueYear), 10);

    const options = [];
    for (let m = 1; m <= 12; m++) {
      if (selectedYear === currentYear && m < currentMonth) continue;
      const val = String(m).padStart(2, "0");
      options.push(
        <SelectItem key={val} value={val}>
          {val}
        </SelectItem>
      );
    }
    return options;
  }, [dueYear]);

  useEffect(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const selectedYear = parseInt(String(dueYear), 10);

    if (selectedYear === currentYear && parseInt(dueMonth, 10) < currentMonth) {
      setDueMonth("");
    }
  }, [dueYear]);

  const brandBadge = (() => {
    if (!cardInfo.card) return null;
    const brands: Record<string, { name: string; bg: string }> = {
      'visa': { name: 'Visa', bg: 'bg-[#1a1f71]' },
      'mastercard': { name: 'Mastercard', bg: 'bg-[#eb001b]' },
      'american-express': { name: 'Amex', bg: 'bg-[#006fcf]' },
      'elo': { name: 'Elo', bg: 'bg-[#000]' },
      'hipercard': { name: 'Hiper', bg: 'bg-[#822124]' },
    };
    const brand = brands[cardInfo.card.type];
    if (!brand) return null;
    return (
      <span className={`${brand.bg} inline-flex items-center rounded px-2 py-0.5 text-[11px] font-semibold tracking-wide text-white`}>
        {brand.name}
      </span>
    );
  })();

  return (
    <div className="rounded-xl border border-gray-200 bg-linear-to-b from-white to-gray-50/30 px-5 pb-2 pt-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-gray-500">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
            <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
          </svg>
          <span className="text-xs font-medium">Pagamento seguro</span>
        </div>
        <div className="flex items-center gap-1">
          {brandBadge ?? (
            <span className="text-[11px] text-gray-400">Visa, Master, Amex, Elo</span>
          )}
        </div>
      </div>

      <Column size={12} className="pb-5">
        <Input
          mask={new Array(255).fill(/\D/)}
          name="payment.mainName"
          placeholder="Nome do titular no cartão"
          value={name}
          helperText="Como está gravado no cartão."
          onChange={(e) => setName(e.target.value)}
          onBlur={(e) => setName(e.target.value)}
        />
      </Column>

      <Column size={12} className="pb-5">
        <Input
          type="tel"
          mask={cardMask}
          name="payment.creditCard"
          placeholder="Número do cartão"
          value={creditCard}
          onChange={(e) => setCreditCard(e.target.value)}
          onBlur={(e) => setCreditCard(e.target.value)}
        />
      </Column>

      <div className="flex w-full items-start gap-3 pb-5">
        <div className="flex-1">
          <Select
            name="payment.dueMonth"
            value={dueMonth}
            placeholder="Mês"
            onChange={(e) => setDueMonth(e.target.value)}
            onBlur={(e) => setDueMonth(e.target.value)}
          >
            {monthOptions}
          </Select>
        </div>
        <div className="flex-1">
          <Select
            name="payment.dueYear"
            value={dueYear}
            placeholder="Ano"
            onChange={(e) => setDueYear(e.target.value)}
            onBlur={(e) => setDueYear(e.target.value)}
          >
            {yearsOptions}
          </Select>
        </div>
        <div className="flex-1">
          <Input
            type="tel"
            mask={cvvMask}
            name="payment.securityCode"
            placeholder={isAmex ? 'CID (4 dig.)' : 'CVV'}
            value={securityCode}
            helperText={isAmex ? 'Frente do cartão' : 'Verso do cartão'}
            onChange={(e) => setSecurityCode(e.target.value)}
            onBlur={(e) => setSecurityCode(e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

export default CreditCardPaymentFields;
